import { Controller } from "@hotwired/stimulus";

import { DESKTOP_MIN_WIDTH, prefersReducedMotion } from "~/lib/viewport";
import { clamp01 } from "~/lib/web/ease";
import { registerScrub } from "~/lib/web/scrub";

/** How much of each card is visible past the one in front of it. */
const PEEK = 0.52;

/** Pixels of scroll the fan-out takes to complete. */
const ENTER = 560;

export default class WebPersonasController extends Controller<HTMLElement> {
  static override targets = ["card"];
  declare readonly cardTargets: HTMLElement[];

  private unregister: (() => void) | null = null;
  private observer: ResizeObserver | null = null;
  private isDesktop = false;
  private peek = 0;
  private gaps: number[] = [];

  override connect(): void {
    const [first] = this.cardTargets;
    if (first === undefined || this.cardTargets.length < 2) return;
    if (prefersReducedMotion()) return;

    /* These are layout values and none of them change with the scroll:
       offsetLeft and offsetWidth ignore the transforms written below. Measured
       here and on resize, the scrub callback only writes, which is the split
       registerScrub documents. */
    this.measure();
    this.observer = new ResizeObserver(() => {
      this.measure();
      // A resize is the only way the viewport crosses back under the
      // breakpoint, so this is where transforms left on the cards are cleared.
      if (!this.isDesktop) this.clearCards();
    });
    /* The first card rather than the row: the landing stylesheet is linked from
       the body, so connect() can measure before it applies, and it is the card
       widths that change when it does. The row is full width either way. */
    this.observer.observe(first);

    this.unregister = registerScrub(first, (rect, viewportHeight) => {
      if (!this.isDesktop) return;
      const progress = clamp01((viewportHeight - rect.top) / ENTER);
      this.cardTargets.forEach((card, index) => {
        const gap = this.gaps[index] ?? 0;
        const pull = (gap - index * this.peek) * (1 - progress);
        card.style.transform = `translate3d(${(-pull).toFixed(1)}px, 0, 0)`;
      });
    });
  }

  override disconnect(): void {
    this.unregister?.();
    this.unregister = null;
    this.observer?.disconnect();
    this.observer = null;
    this.clearCards();
  }

  private measure(): void {
    const [first] = this.cardTargets;
    if (first === undefined) return;
    this.isDesktop = window.innerWidth >= DESKTOP_MIN_WIDTH;
    this.peek = first.offsetWidth * PEEK;
    this.gaps = this.cardTargets.map(
      (card) => card.offsetLeft - first.offsetLeft,
    );
  }

  private clearCards(): void {
    for (const card of this.cardTargets) card.style.transform = "";
  }
}
